import {fromSeconds, parseDate} from 'common/date';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {Button} from 'src/Button';
import {
  formatCents,
  formatCentsShort,
  formatCentsShortSigned,
  formatCentsSigned,
  formatDateRange,
  formatNetShort,
} from 'src/CardBenefitsView/benefitUtils';
import {nettedSpans} from 'src/CardBenefitsView/benefitWindows';
import {FeeYearGrid} from 'src/CardBenefitsView/FeeYearGrid';
import {useModals} from 'src/Modals';
import type {FeeYearStats} from 'src/CardBenefitsView/benefitWindows';

/**
 * Compact posted/booked/open/missed meter against the year's potential.
 * Missed pins to the right edge so a lapse reads as the gap it left.
 */
export const YearMeter = ({stats}: {stats: FeeYearStats}) => {
  if (stats.potential <= 0) {
    return <div className="bg-muted/40 h-1.5 rounded-full" />;
  }
  const pct = (cents: number): string =>
    `${Math.max(0, Math.min(100, (cents / stats.potential) * 100))}%`;
  const spans = nettedSpans(stats);
  return (
    <div
      className="bg-muted/40 flex h-1.5 overflow-hidden rounded-full"
      title={`${formatCents(Math.max(0, stats.captured))} of ${formatCents(stats.potential)} captured`}
    >
      <span className="bg-green-500/50" style={{width: pct(spans.posted)}} />
      <span className="stripe-booked" style={{width: pct(spans.booked)}} />
      <span className="bg-blue-400/60" style={{width: pct(stats.open)}} />
      <span className="flex-1" />
      <span className="stripe-missed" style={{width: pct(stats.missed)}} />
    </div>
  );
};

type Props = {
  stats: FeeYearStats;
  defaultExpanded: boolean;
  previousFeePeriodId: string | null;
  onUpdate: () => void;
  onCopyFromPrevious: (
    sourceFeePeriodId: string,
    targetFeePeriodId: string,
  ) => Promise<void> | void;
};

/**
 * One fee year: a scannable summary row (mini meter, captured/cap, missed,
 * net) that expands into the fee-year grid plus the year's manage actions.
 * The current year starts expanded; history reads as a stack of quiet rows.
 */
export const FeeYearSection = (props: Props) => {
  const {stats, defaultExpanded, previousFeePeriodId, onUpdate, onCopyFromPrevious} =
    props;
  const {feePeriod, isCurrent} = stats;
  const {editFeePeriodModal, deleteFeePeriodModal, createCardBenefitModal} =
    useModals();
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const [copying, setCopying] = React.useState(false);
  // An edit can move a period across the current/past boundary while the
  // component keeps its key (so the initializer won't re-run). Re-sync the
  // default expansion when that flips, without clobbering manual toggles.
  React.useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  const startYear = fromSeconds(feePeriod.startDate).year;
  const hasBenefits = feePeriod.benefits.length > 0;
  const showCopyButton = !hasBenefits && previousFeePeriodId != null;

  const handleCopy = async () => {
    if (previousFeePeriodId != null && !copying) {
      setCopying(true);
      try {
        await onCopyFromPrevious(previousFeePeriodId, feePeriod._id);
      } finally {
        setCopying(false);
      }
    }
  };

  return (
    <div className="border-border bg-card rounded-lg border">
      <div className="flex items-center gap-1 pr-2">
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          // Narrow screens keep only the year label and net; the meter and
          // captured/missed columns return at lg (the app shell keeps a 300px sidebar above the sm breakpoint)
          className="grid flex-1 cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-2.5 text-left lg:grid-cols-[minmax(10rem,14rem)_1fr_7.5rem_5rem_6rem]"
        >
          <span className="flex min-w-0 items-center gap-1.5">
            {expanded ? (
              <ChevronDown className="text-muted-foreground size-3.5 shrink-0" />
            ) : (
              <ChevronRight className="text-muted-foreground size-3.5 shrink-0" />
            )}
            <span className="text-sm font-semibold whitespace-nowrap">
              FY {startYear}
            </span>
            {isCurrent && (
              <span className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-[10px] font-medium">
                Current
              </span>
            )}
            <span className="text-muted-foreground truncate text-xs">
              {formatCents(feePeriod.amount)} fee
            </span>
          </span>
          <div className="hidden lg:block">
            <YearMeter stats={stats} />
          </div>
          <span className="hidden text-right text-xs tabular-nums lg:block">
            <span className="font-medium">
              {formatCentsShortSigned(stats.captured)}
            </span>
            <span className="text-muted-foreground">
              {' '}
              / {formatCentsShort(stats.potential)}
            </span>
          </span>
          <span className="hidden text-right text-xs tabular-nums lg:block">
            {stats.missed > 0 ? (
              <span className="text-amber-600 dark:text-amber-400">
                −{formatCentsShort(stats.missed)}
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </span>
          <span
            className={`text-right text-xs font-medium tabular-nums ${
              stats.net >= 0
                ? 'text-positive'
                : 'text-destructive'
            }`}
          >
            net {formatNetShort(stats.net)}
          </span>
        </button>
        {expanded && (
          <span className="flex shrink-0 gap-0.5">
            {showCopyButton && (
              <Button
                size="icon"
                variant="ghost"
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={handleCopy}
                disabled={copying}
                title="Copy benefits from previous period"
              >
                <Copy className="h-4 w-4" />
              </Button>
            )}
            <Button
              size="icon"
              variant="ghost"
              title="Add benefit"
              onClick={() =>
                createCardBenefitModal.openModal({
                  feePeriodId: feePeriod._id,
                  feePeriodStartDate: feePeriod.startDate,
                  feePeriodEndDate: feePeriod.endDate,
                  cardAccountId: feePeriod.accountId,
                  onSuccess: onUpdate,
                })
              }
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              title="Edit fee period"
              onClick={() =>
                editFeePeriodModal.openModal({feePeriod, onSuccess: onUpdate})
              }
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              title="Delete fee period"
              onClick={() =>
                deleteFeePeriodModal.openModal({feePeriod, onDeleted: onUpdate})
              }
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </span>
        )}
      </div>

      {expanded && (
        <>
          <p className="text-muted-foreground border-border border-t px-4 py-1.5 pl-[38px] text-[11px]">
            {formatDateRange(feePeriod.startDate, feePeriod.endDate)}
            {' · '}
            {feePeriod.rawTransaction != null ? (
              <Link
                to={`/raw-transactions/${feePeriod.rawTransaction._id}`}
                className="hover:text-foreground"
              >
                fee {parseDate(feePeriod.rawTransaction.date).toLocaleString({
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}{' '}
                · {feePeriod.rawTransaction.description} ·{' '}
                {formatCents(feePeriod.rawTransaction.amount)}
              </Link>
            ) : (
              <>fee transaction: {feePeriod.rawTransactionId}</>
            )}
            {stats.uncovered !== 0 && (
              <span className="text-amber-600 dark:text-amber-400">
                {' '}
                · {formatCentsSigned(stats.uncovered)} linked outside windows
              </span>
            )}
          </p>
          {hasBenefits ? (
            // The grid sets its own min width (it scales with the axis); on
            // narrow screens it scrolls inside this container, not the page
            <div className="border-border overflow-x-auto border-t pt-2 pb-1">
              <FeeYearGrid feePeriod={feePeriod} onUpdate={onUpdate} />
            </div>
          ) : (
            <div className="border-border text-muted-foreground border-t px-4 py-4 text-center text-sm">
              No benefits for this period yet.
              {showCopyButton && ' Use the copy button to bring last year’s over.'}
            </div>
          )}
        </>
      )}
    </div>
  );
};
